import React, { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { Loader } from 'components';
import PrivateRoute from './PrivateRoute';
import RootLayout from './RootLayout';
import PdfViewer from 'modules/fund/container/PdfViewer';
import PdfViewerData from 'modules/fund/container/PdfViewerData';
import ErrorBoundaryFallback from 'error-boundary';

const UIControls = lazy(() => import('ui-controls/UIControls'));
const UIComponents = lazy(() => import('components/UIComponents'));
const Login = lazy(() => import('../modules/login/container'));
const ResetPwd = lazy(() => import('../modules/changePwd/container'));
const InitiateChangePwd = lazy(
  () => import('../modules/changePwd/components/InitiateChangePwd')
);
const VerificationCodePage = lazy(
  () => import('../modules/changePwd/components/VerificationCodePage')
);
const Dashboard = lazy(() => import('../modules/home/container'));
const InvestmentsPageWIP = lazy(
  () => import('../modules/investments-wip/container')
);
const Fund = lazy(() => import('../modules/fund/container'));
const Error404 = lazy(() => import('./Error404'));
const PacingPlan = lazy(() => import('../modules/PacingPlan/container/index'));
const LandingPage = lazy(
  () => import('../modules/landingPage/container/index')
);
const FundManagerPage = lazy(
  () => import('../modules/fundManager/container/index')
);

const MarkdownViewer = lazy(
  () => import('../modules/markdownViewer/MarkdownViewer')
);
const CompanyAssetsPage = lazy(
  () => import('../modules/companyEntities/container/index')
);

const FundManagerDetails = lazy(
  () => import('../modules/fundManagerDetails/container/index')
);

const CompanyDetailsPage = lazy(
  () => import('../modules/companyAssetsDetails/container/index')
);

const FundContactPage = lazy(
  () => import('../modules/contactPage/container/index')
);

const ContactDetailPage = lazy(
  () => import('../modules/contactDetails/container/index')
);

const FundDocumentPage = lazy(
  () => import('../modules/fundDocument/container/FundDocument')
);

export const routes = createRoutesFromElements(
  <Route
    path="/"
    element={<RootLayout />}
    errorElement={<ErrorBoundaryFallback />}
  >
    <Route path="login" element={<Login />} />
    <Route path="resetPwd" element={<InitiateChangePwd />} />
    <Route path="changePwd/:token" element={<ResetPwd />} />
    <Route path="validatePwd/:token" element={<VerificationCodePage />} />
    <Route
      path=""
      element={
        <PrivateRoute>
          <LandingPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/funds"
      element={
        <PrivateRoute>
          <Dashboard />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/ai-ic-report-viewer"
      element={
        <PrivateRoute>
          <MarkdownViewer />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/fund-managers"
      element={
        <PrivateRoute>
          <FundManagerPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/companies"
      element={
        <PrivateRoute>
          <CompanyAssetsPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route path="/assets" element={<Navigate to="/companies" replace />} />
    <Route
      path="/company"
      element={
        <PrivateRoute>
          <CompanyDetailsPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/asset"
      element={<Navigate to={`/company${window.location.search}`} replace />}
    />
    <Route
      path="/fund-manager"
      element={
        <PrivateRoute>
          <FundManagerDetails />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/contacts"
      element={
        <PrivateRoute>
          <FundContactPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/person"
      element={
        <PrivateRoute>
          <ContactDetailPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/reporting"
      element={
        <PrivateRoute>
          <Dashboard />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/documents"
      element={
        <PrivateRoute>
          <FundDocumentPage />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="fund-review/:id"
      element={
        <PrivateRoute>
          <Fund />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="black-book"
      element={
        <PrivateRoute>
          <PdfViewer />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="black-book/:id"
      element={
        <PrivateRoute>
          <PdfViewerData />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="pacing-plan"
      element={
        <PrivateRoute>
          <PacingPlan />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="fund"
      element={
        <PrivateRoute>
          <Fund />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    <Route
      path="/portfolios"
      element={
        <PrivateRoute>
          <InvestmentsPageWIP />
        </PrivateRoute>
      }
      errorElement={<ErrorBoundaryFallback />}
    />
    {process.env.REACT_APP_ENABLE_DEV_PAGES === 'true' && (
      <Route path="ui-controls" element={<UIControls />} />
    )}
    {process.env.REACT_APP_ENABLE_DEV_PAGES === 'true' && (
      <Route path="ui-components" element={<UIComponents />} />
    )}
    <Route path="*" element={<Error404 />} />
  </Route>
);

const router = createBrowserRouter(routes);

const Routing = () => (
  <Suspense fallback={<Loader />}>
    <RouterProvider router={router} />
  </Suspense>
);

// Named exports for Routing and router
export { Routing, router };
